import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    Renderer2, SimpleChanges,
    ViewChild, ViewContainerRef
} from '@angular/core';
import { SipoService } from "../services/SipoService";
import { PdfDto } from "../model/pdf-dto";
import { CommonModule, NgIf } from "@angular/common";
import { TranslateModule } from '@ngx-translate/core';
import { PdfSize, Position } from "../model/position";
import { SignerModel } from '../model/signer-model';
import { IdCardComponent } from "../../shared/components/id-card/id-card.component";
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { EvidenceSvgPipe } from "../../shared/pipes/evidence-svg.pipe";
import { UserProperties, UserPropertiesService } from "../services/user-properties.service";

export interface DraggableData {
    signer: string;
    position?: Position;
    size?: PdfSize;
    /** Seite (1-basiert), auf der der Drop erfolgt ist */
    pageNumber?: number;
    /** Anzeigegröße der Seite (CSS-Pixel) zum Drop-Zeitpunkt */
    displayW?: number;
    displayH?: number;
}

export interface SignPositionEvent {
    signer: string;
    positions?: Array<Position | undefined>;
}

@Component({
    selector: 'app-drag-drop-scroll',
    standalone: true,
    imports: [
        CommonModule,
        ProgressSpinnerModule,
        TranslateModule,
        NgIf
    ],
    providers: [
        EvidenceSvgPipe
    ],
    templateUrl: "drag-drop-scroll.component.html",
    styleUrls: ["drag-drop-scroll.component.scss"],
})
export class DragDropScrollComponent implements OnInit {

    @Input() pdfId!: string;
    @Input() processId!: string;
    @Input() addedPeople!: { index: number, email: string, fullName: string, isCreator: boolean }[];
    @Input() signerModel!: SignerModel;
    @Input() isCloseTriggered!: boolean;

    @Output() outPdfSize = new EventEmitter<any>();
    @Output() outSignPosition = new EventEmitter<SignPositionEvent[]>();
    @Output() outStepsAvailability = new EventEmitter<boolean>(false);
    @Output() outIsLoading = new EventEmitter<boolean>();

    @ViewChild('droppableContainerRef', { read: ViewContainerRef, static: true })
    droppableContainerRef!: ViewContainerRef;

    @ViewChild('droppableContainerRef', { read: ElementRef, static: true })
    droppableContainerElementRef!: ElementRef;

    pdfDto!: PdfDto;
    isLoading: boolean = true;
    @Input() pdfFileUploadingProcessTrack!: 'success' | 'error' | 'progress';
    currentScreenWidth!: number;
    private draggableMap: Map<string, DraggableData> = new Map();

    constructor(
        private el: ElementRef,
        private renderer: Renderer2,
        private cdRef: ChangeDetectorRef,
        private sipoService: SipoService,
        private evidenceSvgPipe: EvidenceSvgPipe,
        private userPropertyService: UserPropertiesService
    ) {}

    ngOnInit() {
        this.currentScreenWidth = window.innerWidth;
        this.getCreatorIdCardInfo();

        if (this.processId && this.processId.length > 0) {
            this.getImages();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.processId || this.processId.length === 0) {
            if (changes['pdfFileUploadingProcessTrack'] && this.pdfFileUploadingProcessTrack === 'success') {
                this.getImages();
            }
        }
        if (changes['isCloseTriggered']) {
            this.callEmitEvents();
        }
    }

    getImages() {
        const onSuccess = (response: PdfDto) => {
            response.images.sort((a, b) => a.pageNumber - b.pageNumber);
            this.pdfDto = response;
            this.createDraggableDivs();
            this.isLoading = false;
            this.outIsLoading.emit(this.isLoading);
            this.cdRef.detectChanges();
        };
        const onError = (error: any) => {
            console.error("Error fetching images:", error);
            this.isLoading = false;
            this.outIsLoading.emit(this.isLoading);
        };

        if (!this.processId || this.processId.length === 0) {
            this.sipoService.getImages(this.pdfId).subscribe(onSuccess, onError);
        } else {
            this.sipoService.getImagesByProcess(this.processId).subscribe(onSuccess, onError);
        }
    }

    createDraggableDivs(): void {
        const droppableContainer = this.el.nativeElement.querySelector('.drag-items');
        if (!this.pdfDto || !this.pdfDto.images || !this.addedPeople || this.addedPeople.length === 0) {
            console.warn("No PDF data or signers available.");
            return;
        }

        const signerPositionsMap = new Map<string, Position[]>();
        if (this.signerModel?.signpositions) {
            this.signerModel.signpositions.forEach(signPosition => {
                if (!signerPositionsMap.has(signPosition.signer)) {
                    signerPositionsMap.set(signPosition.signer, []);
                }
                signerPositionsMap.get(signPosition.signer)?.push({
                    x: signPosition.x,
                    y: signPosition.y,
                    pageNumber: (signPosition as any).pageNumber,    // falls vorhanden
                    displayWidth: (signPosition as any).displayW,        // falls vorhanden
                    displayHeight: (signPosition as any).displayH         // falls vorhanden
                });
            });
        }

        this.addedPeople.forEach((person, signerIndex) => {
            if (!person.email || !person.fullName) {
                console.warn("Skipping empty signer entry");
                return;
            }

            const idCardContainerDiv = document.createElement("div");
            const resetButton = document.createElement("button");
            resetButton.innerHTML = "<i class='pi pi-times text-red-500 font-bold'></i>";
            resetButton.classList.add(
                'absolute','top-0','right-0','p-2','cursor-pointer','border-none','bg-transparent'
            );
            resetButton.addEventListener('click', () => {
                idCardContainerDiv.style.left = '';
                this.setStyleForDraggable(idCardContainerDiv, signerIndex);
                this.isAllIdCardsPlaced();
            });

            idCardContainerDiv.classList.add('drag-item-dragging', 'draggable', 'w-fit');
            idCardContainerDiv.setAttribute('draggable', 'true');

            const draggableIdCardComponentRef = this.droppableContainerRef.createComponent(IdCardComponent);
            const idCardComponentAsNativeEl = draggableIdCardComponentRef.location.nativeElement as HTMLElement;
            draggableIdCardComponentRef.instance.name = person.isCreator ? this.loggedInUserIdCardInfo.name : person.fullName;
            draggableIdCardComponentRef.instance.signType = this.signerModel?.signingType;
            draggableIdCardComponentRef.instance.optionalLine1 = person.isCreator ? this.loggedInUserIdCardInfo.optionalLine1 : '';
            draggableIdCardComponentRef.instance.optionalLine2 = '';
            draggableIdCardComponentRef.instance.currentTime = person.isCreator ? this.loggedInUserIdCardInfo.time : '';
            draggableIdCardComponentRef.instance.previewUrl = person.isCreator ? this.loggedInUserIdCardInfo.image : '';
            draggableIdCardComponentRef.instance.customWidth = 15;

            const uniqueId = `draggable-${signerIndex}`;
            idCardContainerDiv.setAttribute('id', uniqueId);

            const positionsForSigner = signerPositionsMap.get(person.email);
            const positionForPage = positionsForSigner && positionsForSigner[0];

            const draggableData: DraggableData = {
                signer: person.email,
                position: positionForPage
            };
            this.draggableMap.set(uniqueId, draggableData);

            if (positionForPage) {
                const pageNum = positionForPage.pageNumber ?? 1;
                const pageEl: HTMLElement | null =
                    this.el.nativeElement.querySelector(`.pdf-page[data-page-number="${pageNum}"]`);
                if (pageEl) {
                    pageEl.appendChild(idCardContainerDiv);
                    idCardContainerDiv.style.position = 'absolute';
                    idCardContainerDiv.style.left = `${positionForPage.x}px`;
                    idCardContainerDiv.style.top = `${positionForPage.y}px`;
                } else {
                    this.setStyleForDraggable(idCardContainerDiv, signerIndex);
                }
            } else {
                this.setStyleForDraggable(idCardContainerDiv, signerIndex);
            }

            idCardContainerDiv.appendChild(resetButton);
            idCardContainerDiv.appendChild(idCardComponentAsNativeEl)
            droppableContainer.appendChild(idCardContainerDiv);

            this.addDragAndDropListeners(idCardContainerDiv);
        });
    }

    getDroppedDraggablesGroupedBySigner(): Map<string, DraggableData[]> {
        const groupedMap = new Map<string, DraggableData[]>();
        this.draggableMap.forEach((value) => {
            if (value.position) {
                if (!groupedMap.has(value.signer)) {
                    groupedMap.set(value.signer, []);
                }
                groupedMap.get(value.signer)?.push(value);
            }
        });
        return groupedMap;
    }

    setStyleForDraggable(div: HTMLElement, signerIndex: number) {
        const yOffset = signerIndex * 90;
        this.renderer.addClass(div, 'fixed');
        this.renderer.setStyle(div, 'top', `${100 + yOffset}px`);
        this.renderer.setStyle(div, 'right', `50px`);
    }

    addDragAndDropListeners(draggable: HTMLElement): void {
        let offsetX = 0, offsetY = 0, isDragging = false;

        this.renderer.listen(draggable, 'dragstart', (e: DragEvent) => {
            isDragging = true;
            const rect = draggable.getBoundingClientRect();
            offsetX = e.clientX - rect.left;
            offsetY = e.clientY - rect.top;
            this.renderer.setStyle(draggable, 'opacity', '0.7');
            e.dataTransfer?.setData('text/plain', draggable.id);
        });

        this.renderer.listen(this.el.nativeElement, 'dragover', (e: DragEvent) => {
            e.preventDefault();
        });

        this.renderer.listen(this.el.nativeElement, 'drop', (e: DragEvent) => {
            e.preventDefault();
            if (!isDragging) return;

            // Seite unter dem Cursor ermitteln
            const elAtPoint = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
            const pageEl = elAtPoint?.closest('.pdf-page') as HTMLElement | null;
            if (!pageEl) {
                console.warn('Drop außerhalb einer PDF-Seite.');
                isDragging = false;
                return;
            }

            // Anzeigegröße der Seite (CSS-Pixel)
            const pageRect = pageEl.getBoundingClientRect();
            const displayW = pageRect.width;
            const displayH = pageRect.height;

            // Seitenindex (1-basiert)
            const pageNumberAttr = pageEl.getAttribute('data-page-number');
            const pageNumber = pageNumberAttr ? parseInt(pageNumberAttr, 10) : 1;

            // Position relativ zur Seite (oben-links)
            const xRel = Math.max(0, e.clientX - offsetX - pageRect.left);
            const yRel = Math.max(0, e.clientY - offsetY - pageRect.top);

            // Draggable in diese Seite einhängen
            if (draggable.parentElement !== pageEl) {
                pageEl.appendChild(draggable);
            }

            // Styles setzen
            this.renderer.setStyle(draggable, 'left', `${xRel}px`);
            this.renderer.setStyle(draggable, 'top', `${yRel}px`);
            this.renderer.setStyle(draggable, 'position', 'absolute');
            this.renderer.setStyle(draggable, 'opacity', '1');
            this.renderer.removeClass(draggable, 'fixed');

            // Position inkl. pageNumber + displayW/H speichern
            this.draggableMap.set(draggable.id, {
                ...this.draggableMap.get(draggable.id)!,
                position: { x: xRel, y: yRel, pageNumber, displayWidth: displayW, displayHeight: displayH }
            });

            isDragging = false;
            this.isAllIdCardsPlaced();
        });
    }


    //ToDo: rausnehmen...
    getPdfRenderedSize(): PdfSize {
        const pdfDocument = this.el.nativeElement.querySelector('.pdf-document');
        let pdfSize: PdfSize = { width: 0, height: 0};
        if (!pdfDocument) {
            console.warn("PDF-Dokument nicht gefunden.");
            return pdfSize;
        }
        return {
            height: pdfDocument.clientHeight,
            width: pdfDocument.clientWidth
        };
    }

    isAllIdCardsPlaced() {
        const el: HTMLElement = this.droppableContainerElementRef.nativeElement as HTMLElement;
        const count = el.querySelectorAll('.fixed').length;
        this.outStepsAvailability.emit(count <= 1);
    }

    // ID-Card Infos (Creator)
    loggedInUserIdCardInfo = {
        name: '',
        optionalLine1: '',
        optionalLine2: '',
        image: '',
        time: '',
    }

    getCreatorIdCardInfo() {
        this.userPropertyService.getAllByGroup('stamp').subscribe({
            next: (values: UserProperties[]) => {
                values.forEach((value) => {
                    const key = value.userPropertyKey;
                    const val = value.userPropertyValue;
                    switch (key) {
                        case "sipo.stamp.name":  this.loggedInUserIdCardInfo.name = val; break;
                        case "sipo.stamp.line1": this.loggedInUserIdCardInfo.optionalLine1 = val; break;
                        case "sipo.stamp.line2": this.loggedInUserIdCardInfo.optionalLine2 = val; break;
                        case "sipo.stamp.image": this.loggedInUserIdCardInfo.image = val; break;
                        case "sipo.stamp.time":  this.loggedInUserIdCardInfo.time = val; break;
                    }
                })
            },
            error: (error: Error) => console.error(error)
        })
    }

    callEmitEvents() {
        const groupedDraggables = this.getDroppedDraggablesGroupedBySigner();

        const pdfSize = this.getPdfRenderedSize(); // optional/legacy
        const draggableDataArray: SignPositionEvent[] =
            Array.from(groupedDraggables.entries()).map(([signer, draggables]) => ({
                signer,
                positions: draggables.map(d => d.position)
            }));

        this.outPdfSize.emit(pdfSize);
        this.outSignPosition.emit(draggableDataArray);
    }

    ngOnDestroy(): void {
        this.callEmitEvents()
    }
}
